import { useEffect, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { startCheckout, openCustomerPortal, getPrepaidBalance } from '../lib/membership'
import { getMemberCycleUsage, CYCLE_INCLUDED_LBS } from '../lib/myOrders'
import { getServiceByKey } from '../lib/catalog'
import { showToast } from './Toast'

const CYCLE_LBS = CYCLE_INCLUDED_LBS // re-export for local readability

// MembershipCard — single source of truth for the Ghost Members widget on
// the Account page. Reads member state from `profile` (kept in sync by the
// stripe-webhook) and offers the right CTA:
//   non-member / canceled → Join Ghost Members (Stripe Checkout)
//   active                → Manage subscription  (Stripe Customer Portal)
//   past_due              → Fix payment          (Portal, red bar)
//
// The card never activates/cancels a membership itself — those flows live in
// Stripe and land in the DB via the webhook.
export default function MembershipCard() {
  const { profile } = useAuth()
  const [busy, setBusy] = useState(false)

  const status = profile?.member_status || (profile?.is_member ? 'active' : null)
  const cycleEnd = profile?.member_current_period_end
    ? new Date(profile.member_current_period_end)
    : null
  // Set by the Stripe webhook when the member cancels from the portal: the
  // plan stays active until this date, then the subscription is deleted.
  const cancelAt = profile?.member_cancel_at ? new Date(profile.member_cancel_at) : null

  async function handleJoin() {
    setBusy(true)
    try { await startCheckout() }
    catch (err) { showToast(err?.message || 'Could not open checkout'); setBusy(false) }
  }
  async function handlePortal() {
    setBusy(true)
    try { await openCustomerPortal() }
    catch (err) { showToast(err?.message || 'Could not open portal'); setBusy(false) }
  }

  if (status === 'active') return <ActiveCard cycleEnd={cycleEnd} cancelAt={cancelAt} busy={busy} onPortal={handlePortal} />
  if (status === 'past_due') return <PastDueCard busy={busy} onPortal={handlePortal} />
  if (status === 'canceled') return <CanceledCard cycleEnd={cycleEnd} busy={busy} onJoin={handleJoin} />
  return <PitchCard busy={busy} onJoin={handleJoin} />
}

// ── Non-member ───────────────────────────────────────────────────────

function PitchCard({ busy, onJoin }) {
  return (
    <section className="mt-4 rounded-2xl p-4 text-white" style={{ background: '#0B2038' }}>
      <div className="flex items-baseline justify-between">
        <div className="font-bricolage text-[13px] font-extrabold tracking-tight">
          <span>GHOST</span><span style={{ color: '#1FC0D4' }}> MEMBERS</span>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-white/70">$95 / 4 weeks</div>
      </div>
      <ul className="mt-3 space-y-1 text-[12px] leading-relaxed text-white/85">
        <li>· 50 lbs included every 4 weeks</li>
        <li>· Next-day delivery, priority scheduling</li>
        <li>· Color separation + hypoallergenic detergent on request</li>
        <li>· Cancel anytime</li>
      </ul>
      <button
        type="button"
        onClick={onJoin}
        disabled={busy}
        className="mt-4 w-full rounded-lg bg-white py-2.5 font-bricolage text-[13px] font-extrabold text-navy disabled:opacity-60"
      >
        {busy ? 'Opening checkout…' : 'Join Ghost Members'}
      </button>
    </section>
  )
}

// ── Active member ────────────────────────────────────────────────────

function ActiveCard({ cycleEnd, cancelAt, busy, onPortal }) {
  const [cycle, setCycle] = useState(null) // full shape from getMemberCycleUsage
  const [prepaid, setPrepaid] = useState(null)

  // The overage rate comes from the `services` row, never from a constant in
  // this file. One source — the database — so no screen can quote a price we
  // no longer charge. Until it loads, the rate simply isn't shown.
  const [overagePerLb, setOveragePerLb] = useState(null)
  useEffect(() => {
    let alive = true
    getServiceByKey('members')
      .then((s) => { if (alive) setOveragePerLb((Number(s?.extra_lb_cents) || 0) / 100) })
      .catch(() => { if (alive) setOveragePerLb(null) })
    return () => { alive = false }
  }, [])

  // No cycleEnd argument any more: member_cycle_usage() derives the window
  // from the profile itself, including the first-cycle rule. Passing a date
  // from here is how the card could disagree with the billing RPC.
  const refreshAll = () => {
    getMemberCycleUsage()
      .then(setCycle)
      .catch(() => setCycle({ cycleUsed: 0, cycleIncluded: CYCLE_LBS, cycleLeft: CYCLE_LBS, cycleOverage: 0 }))
    getPrepaidBalance()
      .then((row) => setPrepaid(Number(row?.balance_lbs) || 0))
      .catch(() => setPrepaid(0))
  }

  useEffect(() => {
    let alive = true
    getMemberCycleUsage()
      .then((r) => alive && setCycle(r))
      .catch(() => alive && setCycle({ cycleUsed: 0, cycleIncluded: CYCLE_LBS, cycleLeft: CYCLE_LBS, cycleOverage: 0 }))
    getPrepaidBalance()
      .then((row) => alive && setPrepaid(Number(row?.balance_lbs) || 0))
      .catch(() => alive && setPrepaid(0))
    const onFocus = () => alive && refreshAll()
    window.addEventListener('focus', onFocus)
    return () => { alive = false; window.removeEventListener('focus', onFocus) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleEnd])

  // Display values — always derived from the authoritative shape, never
  // recomputed in the JSX (that's what caused the "3 lbs left when overage" bug).
  const loading = cycle == null
  const cycleUsedR = loading ? null : Math.round(cycle.cycleUsed * 10) / 10
  const cycleLeftR = loading ? null : Math.round(cycle.cycleLeft * 10) / 10
  const cycleOverageR = loading ? null : Math.round(cycle.cycleOverage * 10) / 10
  const overage = !loading && cycle.cycleOverage > 0
  // Against the allowance the DB reported, not the local fallback — so the
  // gauge stays honest if the included lbs ever change.
  const includedR = loading ? CYCLE_LBS : (Number(cycle.cycleIncluded) || CYCLE_LBS)
  const pct = loading ? 0 : Math.min(100, Math.round((cycle.cycleUsed / includedR) * 100))
  const prepaidRounded = prepaid == null ? null : Math.round(prepaid * 10) / 10

  return (
    <>
    <section
      className="mt-4 rounded-2xl p-4 text-navy"
      style={{ background: '#FEF6DF', border: '1px solid #F0DCA0' }}
    >
      <div className="flex items-baseline justify-between">
        <div className="font-bricolage text-[13px] font-extrabold tracking-tight">
          🏆 GHOST MEMBER
        </div>
        <span
          className="text-[9px] font-extrabold uppercase tracking-widest"
          style={{ color: '#B58A0F' }}
        >
          {cancelAt ? 'Ending' : 'Active'}
        </span>
      </div>

      {cancelAt && (
        <div
          className="mt-2 rounded-lg bg-white/70 px-3 py-2 text-[11px] text-navy"
          style={{ border: '1px solid #F0DCA0' }}
        >
          <div className="font-extrabold">
            ⏳ Your membership ends on {cancelAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}.
          </div>
          <div className="mt-0.5 text-ink-2">You can keep using your plan until then.</div>
        </div>
      )}

      {/* ── Cycle usage gauge (monthly 50 lbs) ─────────────────────── */}
      <div className="mt-3">
        <div className="flex items-baseline justify-between gap-3">
          <div className="font-bricolage text-[20px] font-extrabold text-navy">
            {loading
              ? '—'
              : overage
              ? 'Cycle full'
              : `${cycleLeftR} lbs left this cycle`}
          </div>
          <div className="text-[11px] font-bold text-navy/70 whitespace-nowrap">
            {loading ? '' : `${cycleUsedR} / ${includedR} lbs`}
          </div>
        </div>
        <div
          aria-hidden
          className="mt-1.5 h-2 w-full overflow-hidden rounded-full"
          style={{ background: 'rgba(11,32,56,0.12)' }}
        >
          <div
            className="h-full rounded-full"
            style={{
              width: `${pct}%`,
              background: overage ? '#EF4444' : '#0B2038',
              transition: 'width 300ms ease',
            }}
          />
        </div>
        {overage && (
          <p className="mt-1 text-[10px] font-bold text-red">
            {cycleOverageR} lbs over — extra lbs
            {overagePerLb == null ? ' billed on your next invoice.' : ` billed at $${overagePerLb.toFixed(2)}/lb.`}
          </p>
        )}
        {cycleEnd && (
          <p className="mt-1 text-[10px] text-navy/60">
            Cycle ends <b className="text-navy">{fmt(cycleEnd)}</b>
          </p>
        )}
      </div>

      {/* Credit granted BY HAND (a goodwill gesture, a double-charge put
          right). Prepaid lbs are no longer sold — nobody ever bought any at
          $1.75 — but two customers are owed a balance, so it is still shown
          and still consumed. No price, no buy button, and nothing at all
          when the balance is zero. */}
      {prepaidRounded > 0 && (
        <div
          className="mt-3 rounded-xl p-3"
          style={{ background: '#0B2038', color: '#fff' }}
        >
          <div className="text-[9px] font-extrabold uppercase tracking-widest" style={{ color: '#F0DCA0' }}>
            Account credit
          </div>
          <div className="mt-0.5 font-bricolage text-[18px] font-extrabold" style={{ color: '#F0DCA0' }}>
            {prepaidRounded} lbs
          </div>
          <div className="text-[10px] text-white/60">
            Used automatically before any overage. Never expires.
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={onPortal}
        disabled={busy}
        className="mt-3 w-full rounded-lg bg-navy py-2 text-[12px] font-extrabold text-white disabled:opacity-60"
      >
        {busy ? 'Opening portal…' : cancelAt ? 'Keep my membership' : 'Manage subscription'}
      </button>
    </section>
    </>
  )
}

// ── Payment failed ───────────────────────────────────────────────────

function PastDueCard({ busy, onPortal }) {
  return (
    <section
      className="mt-4 rounded-2xl p-4 text-white"
      style={{ background: '#7F1D1D' }}
    >
      <div className="font-bricolage text-[13px] font-extrabold tracking-tight">
        ⚠ PAYMENT FAILED
      </div>
      <p className="mt-2 text-[12px] leading-relaxed text-white/85">
        We couldn't charge your card for the next cycle. Update it to keep
        your Ghost Member benefits.
      </p>
      <button
        type="button"
        onClick={onPortal}
        disabled={busy}
        className="mt-3 w-full rounded-lg bg-white py-2 text-[12px] font-extrabold text-red disabled:opacity-60"
      >
        {busy ? 'Opening portal…' : 'Update payment method'}
      </button>
    </section>
  )
}

// ── Previously canceled ──────────────────────────────────────────────

function CanceledCard({ cycleEnd, busy, onJoin }) {
  // A cancelled member may still have unused prepaid lbs — they're paused,
  // not lost, and come back when the user re-subscribes.
  const [pausedLbs, setPausedLbs] = useState(null)
  useEffect(() => {
    let alive = true
    getPrepaidBalance()
      .then((row) => alive && setPausedLbs(Number(row?.balance_lbs) || 0))
      .catch(() => alive && setPausedLbs(0))
    return () => { alive = false }
  }, [])
  const hasPaused = pausedLbs != null && pausedLbs > 0

  return (
    <section className="mt-4 rounded-2xl p-4 text-white" style={{ background: '#0B2038' }}>
      <div className="flex items-baseline justify-between">
        <div className="font-bricolage text-[13px] font-extrabold tracking-tight">
          <span>GHOST</span><span style={{ color: '#1FC0D4' }}> MEMBERS</span>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-white/70">$95 / 4 weeks</div>
      </div>
      {cycleEnd && (
        <p className="mt-2 text-[11px] text-white/70">
          Your previous membership ended on <b className="text-white">{fmt(cycleEnd)}</b>.
        </p>
      )}
      {hasPaused && (
        <div
          className="mt-3 rounded-lg p-3"
          style={{ background: 'rgba(240,220,160,0.12)', border: '1px solid rgba(240,220,160,0.35)' }}
        >
          <div className="text-[10px] font-extrabold uppercase tracking-widest" style={{ color: '#F0DCA0' }}>
            You have credit on hold
          </div>
          <div className="mt-0.5 font-bricolage text-[18px] font-extrabold" style={{ color: '#F0DCA0' }}>
            {Math.round(pausedLbs * 10) / 10} lbs
          </div>
          <p className="mt-1 text-[11px] text-white/75">
            Re-subscribe to Ghost Members to use them again.
          </p>
        </div>
      )}
      {!hasPaused && (
        <p className="mt-2 text-[12px] leading-relaxed text-white/85">
          Come back any time — same benefits, same price.
        </p>
      )}
      <button
        type="button"
        onClick={onJoin}
        disabled={busy}
        className="mt-3 w-full rounded-lg bg-white py-2.5 font-bricolage text-[13px] font-extrabold text-navy disabled:opacity-60"
      >
        {busy ? 'Opening checkout…' : hasPaused ? 'Reactivate Membership' : 'Rejoin Ghost Members'}
      </button>
    </section>
  )
}

function fmt(d) {
  try { return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) }
  catch { return '' }
}
